import { createLayer, pixelRatioOf, releaseLayer, type CanvasLayer } from './layers';
import { clamp, smoothstep } from './math';

/** Where a light amount falls between two neighbouring key lights. */
export interface KeyBlend {
  /** The key light below (0-based). */
  index: number;
  /** Smoothed 0..1 blend toward key `index + 1`. */
  amount: number;
}

/** Splits a light amount (0..1) across `keys` evenly spaced key lights. */
export const keyBlendOf = (light: number, keys: number): KeyBlend => {
  if (keys <= 1) return { index: 0, amount: 0 };
  const scaled = clamp(light) * (keys - 1);
  const index = Math.min(keys - 2, Math.floor(scaled));
  return { index, amount: smoothstep(0, 1, scaled - index) };
};

export interface KeyedBackdropOptions {
  /** Share of the screen's resolution the paintings are cached at (soft art needs less). */
  scale?: number;
  /** Never sharper than this many device pixels per CSS pixel. */
  maxRatio?: number;
  /** Largest cached painting, in device pixels. */
  maxPixels?: number;
}

/**
 * A full-screen painting cached once per key light and cross-faded between the
 * two keys a light amount falls between. Because a theme's palette is a linear
 * blend of its key lights, the cross-fade matches painting the blended colours
 * directly, while steady frames are two image copies at most.
 *
 * Paintings are made on demand. While animating, at most one missing key is
 * painted per frame (and the nearest painted key stands in), so a key change
 * never costs several paintings in one frame; still frames paint what they need.
 * Keys outside the current pair are released.
 */
export interface KeyedBackdrop {
  /**
   * Draws the blend for `sizeKey` (anything that changes the painting, such as
   * the layout) at full screen size. Returns false when nothing could be drawn.
   */
  draw(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    sizeKey: string,
    blend: KeyBlend,
    paintAll: boolean,
    /** Overall opacity, for drawing the painting again as a reflection or overlay. */
    opacity?: number,
  ): boolean;
  /** Frees every cached painting. */
  release(): void;
}

export const createKeyedBackdrop = (
  keys: number,
  paint: (target: CanvasRenderingContext2D, key: number, width: number, height: number) => void,
  { scale = 1, maxRatio = 2, maxPixels = 3_200_000 }: KeyedBackdropOptions = {},
): KeyedBackdrop => {
  const layers: Array<CanvasLayer | null> = Array.from({ length: keys }, () => null);

  const ready = (key: number, wanted: string) => {
    const layer = layers[key];
    return Boolean(layer && layer.key === wanted && layer.canvas.width > 0);
  };

  const paintKey = (key: number, wanted: string, width: number, height: number, ratio: number) => {
    let layer = layers[key];
    if (!layer) {
      layer = createLayer();
      layers[key] = layer;
    }
    if (!layer) return false;
    const pixelWidth = Math.max(1, Math.ceil(width * ratio));
    const pixelHeight = Math.max(1, Math.ceil(height * ratio));
    if (layer.canvas.width !== pixelWidth) layer.canvas.width = pixelWidth;
    if (layer.canvas.height !== pixelHeight) layer.canvas.height = pixelHeight;
    const c = layer.ctx;
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.globalAlpha = 1;
    c.globalCompositeOperation = 'source-over';
    c.clearRect(0, 0, pixelWidth, pixelHeight);
    c.setTransform(ratio, 0, 0, ratio, 0, 0);
    paint(c, key, width, height);
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.globalAlpha = 1;
    layer.key = wanted;
    layer.ratio = ratio;
    return true;
  };

  return {
    draw(ctx, width, height, sizeKey, blend, paintAll, opacity = 1) {
      if (width <= 0 || height <= 0) return false;
      const deviceRatio = pixelRatioOf(ctx);
      const ratio = Math.max(
        0.25,
        Math.min(deviceRatio * scale, maxRatio, Math.sqrt(maxPixels / Math.max(1, width * height))),
      );
      const wanted = `${sizeKey}|${width}x${height}@${ratio.toFixed(3)}`;
      const base = clamp(blend.index, 0, keys - 1);
      const next = Math.min(keys - 1, base + 1);
      const amount = base === next ? 0 : clamp(blend.amount);
      const needed = amount >= 0.995 ? [next] : amount <= 0.005 ? [base] : [base, next];

      // One missing key per animated frame (the nearest painted one stands in for the other).
      let painted = 0;
      needed.forEach((key) => {
        if (ready(key, wanted)) return;
        if (paintAll || painted === 0) {
          if (paintKey(key, wanted, width, height, ratio)) painted += 1;
        }
      });

      layers.forEach((layer, key) => {
        if (layer && key !== base && key !== next) {
          releaseLayer(layer);
          layers[key] = null;
        }
      });

      const drawLayer = (key: number, alpha: number) => {
        const layer = layers[key];
        if (!layer || layer.canvas.width === 0 || alpha <= 0.003) return false;
        ctx.globalAlpha = Math.min(1, alpha) * opacity;
        ctx.drawImage(layer.canvas, 0, 0, width, height);
        return true;
      };

      const baseReady = ready(base, wanted);
      const nextReady = ready(next, wanted);
      let drawn = false;
      if (needed.length === 1) {
        const only = needed[0];
        drawn = ready(only, wanted) ? drawLayer(only, 1) : drawLayer(only === base ? next : base, 1);
      } else if (baseReady && nextReady) {
        drawn = drawLayer(base, 1);
        drawLayer(next, amount);
      } else {
        drawn = drawLayer(baseReady ? base : next, 1);
      }
      ctx.globalAlpha = 1;
      return drawn;
    },
    release() {
      layers.forEach((layer, key) => {
        releaseLayer(layer);
        layers[key] = null;
      });
    },
  };
};
